import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  Inject,
  Optional,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  DialogShellComponent,
  FilledButtonComponent,
  PartnerSelectComponent,
  SelectFieldComponent,
  TextButtonComponent,
  TextFieldComponent,
  TonalButtonComponent,
} from '@custom-components';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { IConfig, ICountry, NgxCountriesDropdownModule } from 'ngx-countries-dropdown';
import {
  InstallationGroupWithAccess,
  LocationCreation,
  LocationModel,
  LocationUpdateModel,
  getUserActivePartner,
} from '@core/models';
import { XEMEX_OID } from '@core/constants';
import { LocationService } from '@core/services/location.service';
import { InstallationGroupService } from '@core/services';
import { UserStore } from '@root/app/store/user.store';
import { AuthStore } from '@root/app/store/auth.store';

// With `returnsCreated`, saving closes the dialog with the address that was created, so the caller
// can pick that location, and there is no "save and add another".
type LocationDialogData = {
  mode?: 'create' | 'edit';
  location?: LocationModel;
  returnsCreated?: boolean;
};

export interface CreatedLocation {
  partnerOid: string;
  postalCode: string;
  streetNumber: number;
  streetNumberAddition: string;
}

@Component({
  selector: 'xemex-location-dialog',
  imports: [
    ReactiveFormsModule,
    DialogShellComponent,
    FilledButtonComponent,
    TonalButtonComponent,
    TextButtonComponent,
    TextFieldComponent,
    SelectFieldComponent,
    PartnerSelectComponent,
    NgxCountriesDropdownModule,
    MatSelectModule,
    TranslateModule,
  ],
  templateUrl: './location-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './location-dialog.component.scss',
})
export class LocationDialogComponent {
  readonly dialogRef = inject(MatDialogRef<LocationDialogComponent>);
  private readonly locationService = inject(LocationService);
  private readonly installationGroupService = inject(InstallationGroupService);
  private readonly userStore = inject(UserStore);
  private readonly authStore = inject(AuthStore);
  private readonly destroyRef = inject(DestroyRef);

  readonly mode: 'create' | 'edit';
  readonly returnsCreated: boolean;
  private readonly location?: LocationModel;

  hasSaved = false;
  isSaving = false;

  selectedCountry = 'NL';
  readonly countryConfig: IConfig = { hideDialCode: true };

  // Partner selection — only shown when no partner can be derived (e.g. a Xemex admin/support
  // user, whose active partner is the all-zero placeholder). The picker searches partners by name.
  /** Searchable partner picker; only used when {@link requiresPartnerSelection} is true. */
  readonly partnerControl = new FormControl<string | null>(null);
  /** The partner an admin picked; mirrors {@link partnerControl} as a signal for computeds. */
  readonly selectedPartnerOid = signal<string | null>(null);
  /** No partner could be resolved to assign the location to — shown inline on the field. */
  readonly partnerError = signal(false);

  /** True when no partner can be derived and the user must pick one (e.g. Xemex admin/support). */
  readonly requiresPartnerSelection = computed(() => !this.autoPartnerOid());

  /** True when the user may add locations to installation groups (gates the group selector). */
  readonly canManageInstallationGroups = computed(() => {
    const partnerOid = this.autoPartnerOid();
    const features =
      this.authStore
        .permissions()
        ?.filter(p => p.groupObjectId == partnerOid || p.groupObjectId == XEMEX_OID)
        .flatMap(p => p.roles.flatMap(role => role.features.flatMap(f => f.features)))
        .filter(f => f.featureEnabled)
        .map(f => f.featureName) ?? [];
    return features.includes('manage_installation_groups');
  });

  /** Installation groups the user owns and can attach the new location to. */
  readonly installationGroups = signal<InstallationGroupWithAccess[]>([]);

  /**
   * Save failed after passing client-side validation. `invalid` for a 4xx — the payload itself
   * was rejected, so retrying it unchanged fails the same way; `retry` for a 5xx or network error,
   * where trying again may succeed. Shown inline instead of failing silently.
   */
  readonly saveError = signal<'invalid' | 'retry' | null>(null);

  readonly addForm = new FormGroup({
    street: new FormControl('', Validators.required),
    // The API only accepts a single integer house number (RDT-1384): a range like "51-83" used
    // to pass here silently and fail later with an opaque 422, so it's rejected up front.
    street_number: new FormControl('', [Validators.required, Validators.pattern(/^\d+$/)]),
    street_number_addition: new FormControl(''),
    postalcode: new FormControl('', Validators.required),
    city: new FormControl('', Validators.required),
    installationGroupOids: new FormControl<string[]>([], { nonNullable: true }),
  });

  constructor(@Optional() @Inject(MAT_DIALOG_DATA) data: LocationDialogData | null) {
    this.mode = data?.mode ?? 'create';
    this.location = data?.location;
    this.returnsCreated = data?.returnsCreated ?? false;

    if (this.isEdit && this.location) {
      this.selectedCountry = this.location.locationCountry ?? 'NL';
      this.addForm.patchValue({
        street: this.location.locationStreet ?? '',
        street_number: `${this.location.locationStreetNumber ?? ''}`,
        street_number_addition: this.location.locationStreetNumberAddition ?? '',
        postalcode: this.location.locationPostalCode ?? '',
        city: this.location.locationCity ?? '',
      });
    }

    // Mirror the searchable partner picker into the signal and react to changes: a Xemex admin's
    // chosen partner scopes both the location assignment and the installation-group list.
    this.partnerControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(partnerOid => {
        this.selectedPartnerOid.set(partnerOid || null);
        this.partnerError.set(false);
        // Groups are partner-scoped — drop any prior picks and reload for the chosen partner.
        this.addForm.controls.installationGroupOids.setValue([]);
        this.loadInstallationGroups(this.resolvePartnerOid());
      });

    // Offer the optional installation-group selector to users who may manage groups. For a
    // regular partner user this resolves to their own partner; a Xemex admin has none until
    // they pick one above, so the list loads/refreshes per selected partner.
    this.loadInstallationGroups(this.resolvePartnerOid());

    // Clear a stale save error as soon as the user edits the form, instead of leaving it on
    // screen until the next submit.
    this.addForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.saveError.set(null));
  }

  get isEdit(): boolean {
    return this.mode === 'edit';
  }

  /**
   * Partner derived from the logged-in user's active partner. Xemex admins carry the all-zero
   * placeholder OID ({@link XEMEX_OID}) instead of a real partner, so it's normalized to
   * `undefined` here — they have no derivable partner and must pick one explicitly.
   */
  private autoPartnerOid(): string | undefined {
    const partnerOid = getUserActivePartner(this.userStore.user())?.partnerOid;
    return partnerOid && partnerOid !== XEMEX_OID ? partnerOid : undefined;
  }

  /**
   * The partner the location is assigned to. An explicit dropdown choice always wins, so a
   * Xemex admin's selection is never silently overridden by a derived partner.
   */
  private resolvePartnerOid(): string | undefined {
    return this.selectedPartnerOid() ?? this.autoPartnerOid() ?? undefined;
  }

  /** Loads the owner-access installation groups for the given partner into {@link installationGroups}. */
  private loadInstallationGroups(partnerOid: string | undefined): void {
    if (this.isEdit || !partnerOid || !this.canManageInstallationGroups()) {
      this.installationGroups.set([]);
      return;
    }
    this.installationGroupService
      .getInstallationGroups({ partnerOid, accessVia: 'owner', limit: 100, offset: 0 })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(res => this.installationGroups.set(res.installationGroups));
  }

  onCountryChange(country: ICountry): void {
    this.selectedCountry = country.code;
  }

  onSave(addAnother = false): void {
    this.saveError.set(null);
    if (this.addForm.invalid) {
      this.addForm.markAllAsTouched();
      return;
    }

    const { street, street_number, street_number_addition, postalcode, city } = this.addForm.value;

    if (this.isEdit) {
      const values: LocationUpdateModel = {
        street: street ?? '',
        street_number: Number(street_number),
        street_number_addition: street_number_addition ?? '',
        postalcode: postalcode ?? '',
        city: city ?? '',
        country: this.selectedCountry,
      };
      this.dialogRef.close(values);
      return;
    }

    const partnerOid = this.resolvePartnerOid();
    if (!partnerOid) {
      // No partner to assign to (e.g. a Xemex admin who hasn't picked one) — surface it inline
      // instead of silently doing nothing.
      this.partnerError.set(true);
      return;
    }

    const installationGroupOids = this.addForm.controls.installationGroupOids.value;
    const location: LocationCreation = {
      street: street!,
      street_number: Number(street_number),
      street_number_addition: street_number_addition ?? '',
      postalcode: postalcode!,
      city: city!,
      country: this.selectedCountry,
      ...(installationGroupOids.length ? { installation_group_oids: installationGroupOids } : {}),
    };

    this.isSaving = true;
    this.locationService
      .createLocation(location, partnerOid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isSaving = false;
          this.hasSaved = true;
          if (this.returnsCreated) {
            this.dialogRef.close({
              partnerOid,
              postalCode: postalcode!,
              streetNumber: location.street_number,
              streetNumberAddition: location.street_number_addition ?? '',
            } satisfies CreatedLocation);
          } else if (addAnother) {
            this.addForm.reset();
            this.addForm.markAsUntouched();
            this.selectedCountry = 'NL';
          } else {
            this.dialogRef.close(true);
          }
        },
        error: (error: HttpErrorResponse) => {
          this.isSaving = false;
          this.saveError.set(error.status >= 500 || error.status === 0 ? 'retry' : 'invalid');
        },
      });
  }

  onCancel(): void {
    this.dialogRef.close(this.isEdit ? undefined : this.hasSaved);
  }
}
